// LimbPhysics.ts: Advanced physics system for creature limb movement and interaction
// Integrates with the neural network to create realistic limb behavior

import { Limb, LimbSegment, LimbType } from './limbSystem';
import { Node } from './types';
import { ControlSignal } from './limbControl';
import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';

export interface PhysicsForce {
    x: number;
    y: number;
    z: number;
    type: 'gravity' | 'elasticity' | 'damping' | 'inertia' | 'friction' | 'buoyancy' | 'neural' | 'environmental';
    magnitude: number;
    direction: number;
}

export interface PhysicsState {
    position: { x: number; y: number; z: number };
    velocity: { x: number; y: number; z: number };
    acceleration: { x: number; y: number; z: number };
    angularVelocity: number;
    angularAcceleration: number;
    energy: number;
    temperature: number;
    pressure: number;
}

export interface EnvironmentalField {
    type: 'gravity' | 'magnetic' | 'electric' | 'thermal' | 'pressure' | 'psychic';
    strength: number;
    direction: { x: number; y: number; z: number };
    range: number;
    center: { x: number; y: number; z: number };
    decay: number; // How quickly the field strength decreases with distance
}

export class LimbPhysicsEngine {
    private network: NeuralNetworkSimulator;
    private environmentalFields: EnvironmentalField[] = [];
    private physicsStates: Map<number, PhysicsState> = new Map();
    private forces: Map<number, PhysicsForce[]> = new Map();
    private lastCalculatedForces: Map<string, PhysicsForce[]> = new Map(); // Store forces for Verlet integration
    
    // Physics constants
    private GRAVITY = 0.005; // Minimal gravity - curiosity should win
    private ELASTICITY = 2.0; // Strong elasticity to maintain limb structure
    private DAMPING = 0.8; // Lower damping for more responsiveness
    private INERTIA = 0.7;
    private FRICTION = 0.3;
    private BUOYANCY = 0.05;
    private readonly NEURAL_FORCE_MULTIPLIER = 5.0; // Much stronger neural forces
    
    // Environmental constants
    private readonly AIR_DENSITY = 1.225;
    private readonly WATER_DENSITY = 1000;
    private readonly TEMPERATURE_AMBIENT = 20;
    private readonly PRESSURE_AMBIENT = 101325;
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.initializeEnvironmentalFields();
    }
    
    private initializeEnvironmentalFields(): void {
        // Global gravity field (minimal - curiosity is stronger)
        this.environmentalFields.push({
            type: 'gravity',
            strength: this.GRAVITY * 0.5, // Even weaker global field
            direction: { x: 0, y: 1, z: 0 },
            range: Infinity,
            center: { x: 0, y: 0, z: 0 },
            decay: 0
        });
        
        // Psychic field from the psychonaut creature
        this.environmentalFields.push({
            type: 'psychic',
            strength: 0.5,
            direction: { x: 0, y: 0, z: 1 },
            range: this.network.psychonautCreature.influenceRadius,
            center: { 
                x: this.network.psychonautCreature.x, 
                y: this.network.psychonautCreature.y, 
                z: 0 
            },
            decay: 0.1
        });
        
        // Neural energy field
        this.environmentalFields.push({
            type: 'electric',
            strength: 0.3,
            direction: { x: 0, y: 0, z: 0 },
            range: 200,
            center: { x: 400, y: 300, z: 0 },
            decay: 0.05
        });
    }
    
    // Main physics update method
    public updateLimbPhysics(limb: Limb): void {
        // Initialize physics state if not exists
        if (!this.physicsStates.has(limb.id)) {
            this.initializeLimbPhysics(limb);
        }
        
        // Update each segment
        for (const segment of limb.segments) {
            this.updateSegmentPhysics(segment, limb);
        }
        
        // Apply limb-level physics
        this.applyLimbLevelPhysics(limb);
        
        // Update environmental interactions
        this.updateEnvironmentalInteractions(limb);
    }
    
    private initializeLimbPhysics(limb: Limb): void {
        const state: PhysicsState = {
            position: { x: 0, y: 0, z: 0 },
            velocity: { x: 0, y: 0, z: 0 },
            acceleration: { x: 0, y: 0, z: 0 },
            angularVelocity: 0,
            angularAcceleration: 0,
            energy: limb.currentEnergy,
            temperature: this.TEMPERATURE_AMBIENT,
            pressure: this.PRESSURE_AMBIENT
        };
        
        this.physicsStates.set(limb.id, state);
        this.forces.set(limb.id, []);
    }
    
    private updateSegmentPhysics(segment: LimbSegment, limb: Limb): void {
        // Calculate forces acting on segment
        const forces = this.calculateSegmentForces(segment, limb);
        
        // Store forces for Verlet integration
        this.lastCalculatedForces.set(`${limb.id}-${segment.id}`, forces);
        
        // Apply forces to segment (simplified for Verlet)
        // Note: With Verlet, we don't directly update velocity here
        // The updateSegmentPosition method handles acceleration
        
        // Update segment position based on physics (Verlet integration)
        this.updateSegmentPosition(segment, limb);
        
        // Apply limb-specific physics
        this.applyLimbTypePhysics(segment, limb);
    }
    
    private calculateSegmentForces(segment: LimbSegment, limb: Limb): PhysicsForce[] {
        const forces: PhysicsForce[] = [];
        
        // Gravity force (minimal - curiosity overcomes gravity)
        forces.push({
            x: 0,
            y: this.GRAVITY * segment.thickness * 0.1, // Further reduced
            z: 0,
            type: 'gravity',
            magnitude: this.GRAVITY * segment.thickness * 0.1,
            direction: Math.PI / 2
        });
        
        // Elasticity force (tendency to return to rest position)
        const elasticityForce = this.calculateElasticityForce(segment, limb);
        forces.push(elasticityForce);
        
        // Damping force (resistance to movement)
        const dampingForce = this.calculateDampingForce(segment, limb);
        forces.push(dampingForce);
        
        // Neural force (from neural network activity)
        const neuralForce = this.calculateNeuralForce(segment, limb);
        forces.push(neuralForce);
        
        // CRITICAL FIX: Cluster repulsion force to keep limbs extended
        const clusterRepulsionForce = this.calculateClusterRepulsionForce(segment, limb);
        forces.push(clusterRepulsionForce);
        
        // Environmental forces
        const environmentalForces = this.calculateEnvironmentalForces(segment, limb);
        forces.push(...environmentalForces);
        
        // Limb-specific forces
        const limbSpecificForces = this.calculateLimbSpecificForces(segment, limb);
        forces.push(...limbSpecificForces);
        
        return forces;
    }
    
    private calculateElasticityForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Find the segment's position in the limb hierarchy
        const segmentIndex = limb.segments.findIndex(s => s.id === segment.id);
        
        // If no parent segment exists, use the root node position instead
        if (segmentIndex <= 0 || segment.parentSegment === undefined) {
            const rootNode = this.network.nodes.find(n => n.id === limb.rootNodeId);
            if (!rootNode) {
                return { x: 0, y: 0, z: 0, type: 'elasticity', magnitude: 0, direction: 0 };
            }
            
            // Spring force toward root node position
            const dx = rootNode.x - segment.x;
            const dy = rootNode.y - segment.y;
            const dz = (rootNode.z || 0) - segment.z;
            
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const targetLength = segment.length || 30; // Minimum segment length
            
            // If too close, push away; if too far, pull back
            const distanceFromTarget = distance - targetLength;
            const forceMagnitude = distanceFromTarget * this.ELASTICITY * segment.flexibility;
            
            return {
                x: (distance > 0.01) ? (dx / distance) * forceMagnitude : 0,
                y: (distance > 0.01) ? (dy / distance) * forceMagnitude : 0,
                z: (distance > 0.01) ? (dz / distance) * forceMagnitude : 0,
                type: 'elasticity',
                magnitude: Math.abs(forceMagnitude),
                direction: distance > 0.01 ? Math.atan2(dy, dx) : 0
            };
        }
        
        // Spring force to maintain distance with previous segment
        const prevSegment = limb.segments[segmentIndex - 1];
        const dx = prevSegment.x - segment.x;
        const dy = prevSegment.y - segment.y;
        const dz = prevSegment.z - segment.z;
        
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const targetLength = segment.length || 30;
        
        // Restoring force to maintain target distance
        const distanceFromTarget = distance - targetLength;
        const forceMagnitude = distanceFromTarget * this.ELASTICITY * segment.flexibility;
        
        return {
            x: (distance > 0.01) ? (dx / distance) * forceMagnitude : 0,
            y: (distance > 0.01) ? (dy / distance) * forceMagnitude : 0,
            z: (distance > 0.01) ? (dz / distance) * forceMagnitude : 0,
            type: 'elasticity',
            magnitude: Math.abs(forceMagnitude),
            direction: distance > 0.01 ? Math.atan2(dy, dx) : 0
        };
    }
    
    private calculateDampingForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Damping force opposes movement
        const dampingMagnitude = this.DAMPING * segment.flexibility;
        const vx = segment.vx || 0;
        const vy = segment.vy || 0;
        const vz = segment.vz || 0;
        
        return {
            x: -vx * dampingMagnitude,
            y: -vy * dampingMagnitude,
            z: -vz * dampingMagnitude,
            type: 'damping',
            magnitude: dampingMagnitude,
            direction: Math.atan2(-vy, -vx)
        };
    }
    
    private calculateClusterRepulsionForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // KEY FIX: Push limb segments OUTWARD from dense clusters to maintain extended structure
        const rootNode = this.network.nodes.find(n => n.id === limb.rootNodeId);
        if (!rootNode) {
            return { x: 0, y: 0, z: 0, type: 'environmental', magnitude: 0, direction: 0 };
        }
        
        // Calculate how many nodes are nearby the root (cluster density)
        const nearbyNodes = this.network.nodes.filter(n => {
            const dx = n.x - rootNode.x;
            const dy = n.y - rootNode.y;
            const dz = (n.z || 0) - (rootNode.z || 0);
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            return dist < 100; // Check for dense clusters within 100 units
        });
        
        const clusterDensity = nearbyNodes.length;
        
        // If we're in a dense cluster, push OUTWARD from the cluster center
        if (clusterDensity > 5) {
            // Calculate center of cluster
            const clusterCenter = {
                x: nearbyNodes.reduce((sum, n) => sum + n.x, 0) / nearbyNodes.length,
                y: nearbyNodes.reduce((sum, n) => sum + n.y, 0) / nearbyNodes.length,
                z: nearbyNodes.reduce((sum, n) => sum + (n.z || 0), 0) / nearbyNodes.length
            };
            
            // Push segment away from cluster center
            const dx = segment.x - clusterCenter.x;
            const dy = segment.y - clusterCenter.y;
            const dz = segment.z - clusterCenter.z;
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            
            if (dist > 1e-6) {
                // Strong repulsion force when in dense clusters
                const repulsionStrength = 2.0; // Strong outward force
                const forceMagnitude = repulsionStrength * (clusterDensity / 10); // Stronger when more dense
                
                return {
                    x: (dx / dist) * forceMagnitude,
                    y: (dy / dist) * forceMagnitude,
                    z: (dz / dist) * forceMagnitude * 0.5,
                    type: 'environmental',
                    magnitude: forceMagnitude,
                    direction: Math.atan2(dy, dx)
                };
            }
        }
        
        // For segments extending outward, add gentle push away from root
        const dx = segment.x - rootNode.x;
        const dy = segment.y - rootNode.y;
        const dz = segment.z - (rootNode.z || 0);
        const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
        
        if (dist > 1e-6 && dist < 50) {
            // Small push to keep segments extended
            const extensionForce = 0.3;
            
            return {
                x: (dx / dist) * extensionForce,
                y: (dy / dist) * extensionForce,
                z: (dz / dist) * extensionForce * 0.7,
                type: 'environmental',
                magnitude: extensionForce,
                direction: Math.atan2(dy, dx)
            };
        }
        
        return { x: 0, y: 0, z: 0, type: 'environmental', magnitude: 0, direction: 0 };
    }
    
    private calculateNeuralForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Check if there's an active control signal (exploration target)
        const controlSignal = this.getControlSignalForLimb(limb);
        
        if (controlSignal) {
            // Apply VERY strong force toward exploration target (curiosity wins!)
            const dx = controlSignal.targetX - segment.x;
            const dy = controlSignal.targetY - segment.y;
            const dz = controlSignal.targetZ - segment.z;
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
            
            if (distance > 0.1) {
                // Massive force multiplier for exploration - curiosity overcomes gravity!
                const forceMagnitude = controlSignal.intensity * this.NEURAL_FORCE_MULTIPLIER * 4;
                
                return {
                    x: (dx / distance) * forceMagnitude,
                    y: (dy / distance) * forceMagnitude,
                    z: (dz / distance) * forceMagnitude * 0.7,
                    type: 'neural',
                    magnitude: forceMagnitude,
                    direction: Math.atan2(dy, dx)
                };
            }
        }
        
        // Default neural force (if no active control signal)
        const controlNode = this.network.nodes.find(n => n.id === limb.rootNodeId);
        if (!controlNode) {
            return { x: 0, y: 0, z: 0, type: 'neural', magnitude: 0, direction: 0 };
        }
        
        // Calculate force based on neural activity
        const neuralActivity = controlNode.energy || 0;
        const forceMagnitude = neuralActivity * this.NEURAL_FORCE_MULTIPLIER * limb.effectiveness;
        
        // Direction based on neural network state
        const direction = this.calculateNeuralDirection(controlNode, segment);
        
        return {
            x: Math.cos(direction) * forceMagnitude,
            y: Math.sin(direction) * forceMagnitude,
            z: Math.sin(direction * 1.3) * forceMagnitude * 0.5,
            type: 'neural',
            magnitude: forceMagnitude,
            direction
        };
    }
    
    private getControlSignalForLimb(limb: Limb): ControlSignal | undefined {
        // Access the limb system's control signals
        if (this.network.limbSystem) {
            const controlSystem = this.network.limbSystem.getControlSystem();
            const activeSignals = controlSystem.getActiveSignals();
            return activeSignals.find(s => s.limbId === limb.id);
        }
        return undefined;
    }
    
    private calculateNeuralDirection(controlNode: Node, segment: LimbSegment): number {
        // Calculate direction based on neural network patterns
        const time = this.network.step / 60;
        const baseDirection = Math.atan2(segment.y - controlNode.y, segment.x - controlNode.x);
        
        // Add neural oscillation
        const neuralOscillation = Math.sin(time * 2 + controlNode.id * 0.1) * 0.3;
        
        return baseDirection + neuralOscillation;
    }
    
    private calculateEnvironmentalForces(segment: LimbSegment, limb: Limb): PhysicsForce[] {
        const forces: PhysicsForce[] = [];
        
        for (const field of this.environmentalFields) {
            const distance = this.calculateDistanceToField(segment, field);
            if (distance <= field.range) {
                const fieldStrength = field.strength * Math.exp(-field.decay * distance);
                const force = this.calculateFieldForce(segment, field, fieldStrength);
                forces.push(force);
            }
        }
        
        return forces;
    }
    
    private calculateDistanceToField(segment: LimbSegment, field: EnvironmentalField): number {
        const dx = segment.x - field.center.x;
        const dy = segment.y - field.center.y;
        const dz = segment.z - field.center.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    
    private calculateFieldForce(segment: LimbSegment, field: EnvironmentalField, strength: number): PhysicsForce {
        const dx = field.center.x - segment.x;
        const dy = field.center.y - segment.y;
        const dz = field.center.z - segment.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        
        if (distance < 1e-6) return { x: 0, y: 0, z: 0, type: 'environmental', magnitude: 0, direction: 0 };
        
        const forceMagnitude = strength * segment.thickness;
        const direction = Math.atan2(dy, dx);
        
        return {
            x: (dx / distance) * forceMagnitude,
            y: (dy / distance) * forceMagnitude,
            z: (dz / distance) * forceMagnitude,
            type: 'environmental',
            magnitude: forceMagnitude,
            direction
        };
    }
    
    private calculateLimbSpecificForces(segment: LimbSegment, limb: Limb): PhysicsForce[] {
        const forces: PhysicsForce[] = [];
        
        switch (limb.type) {
            case 'fractal_branch':
                forces.push(this.calculateFractalForce(segment, limb));
                break;
            case 'psychic_manifest':
                forces.push(this.calculatePsychicForce(segment, limb));
                break;
            case 'wave_resonance':
                forces.push(this.calculateWaveForce(segment, limb));
                break;
            case 'energy_conduit':
                forces.push(this.calculateEnergyForce(segment, limb));
                break;
            case 'linguistic_appendage':
                forces.push(this.calculateLinguisticForce(segment, limb));
                break;
            case 'mystical_tendril':
                forces.push(this.calculateMysticalForce(segment, limb));
                break;
            case 'temporal_phase':
                forces.push(this.calculateTemporalForce(segment, limb));
                break;
        }
        
        return forces;
    }
    
    private calculateFractalForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Fractal limbs follow golden ratio patterns
        const goldenRatio = 1.618;
        const time = this.network.step / 60;
        const fractalAngle = (segment.id * goldenRatio * Math.PI) % (Math.PI * 2);
        const fractalMagnitude = Math.sin(time * 0.5 + segment.id * 0.1) * 0.5 + 0.5;
        
        return {
            x: Math.cos(fractalAngle) * fractalMagnitude,
            y: Math.sin(fractalAngle) * fractalMagnitude,
            z: Math.sin(fractalAngle * 1.3) * fractalMagnitude * 0.3,
            type: 'neural',
            magnitude: fractalMagnitude,
            direction: fractalAngle
        };
    }
    
    private calculatePsychicForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Psychic limbs are influenced by psychic potential
        const psychicLevel = this.calculatePsychicPotential();
        const time = this.network.step / 60;
        const psychicAngle = Math.sin(time * 1.5 + segment.id * 0.2) * Math.PI;
        const psychicMagnitude = psychicLevel * 0.8;
        
        return {
            x: Math.cos(psychicAngle) * psychicMagnitude,
            y: Math.sin(psychicAngle) * psychicMagnitude,
            z: Math.cos(psychicAngle * 0.7) * psychicMagnitude * 0.6,
            type: 'neural',
            magnitude: psychicMagnitude,
            direction: psychicAngle
        };
    }
    
    private calculateWaveForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Wave limbs follow wave patterns
        const time = this.network.step / 60;
        const waveFrequency = 2.0;
        const waveAmplitude = 0.6;
        const waveAngle = time * waveFrequency + segment.id * 0.3;
        const waveMagnitude = Math.sin(waveAngle) * waveAmplitude;
        
        return {
            x: Math.cos(waveAngle) * waveMagnitude,
            y: Math.sin(waveAngle) * waveMagnitude,
            z: Math.sin(waveAngle * 1.2) * waveMagnitude * 0.4,
            type: 'neural',
            magnitude: Math.abs(waveMagnitude),
            direction: waveAngle
        };
    }
    
    private calculateEnergyForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Energy limbs are influenced by energy flow
        const energyFlow = this.calculateEnergyFlow();
        const time = this.network.step / 60;
        const energyAngle = time * 0.8 + segment.id * 0.15;
        const energyMagnitude = energyFlow * 0.7;
        
        return {
            x: Math.cos(energyAngle) * energyMagnitude,
            y: Math.sin(energyAngle) * energyMagnitude,
            z: Math.cos(energyAngle * 0.9) * energyMagnitude * 0.5,
            type: 'neural',
            magnitude: energyMagnitude,
            direction: energyAngle
        };
    }
    
    private calculateLinguisticForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Linguistic limbs are influenced by linguistic complexity
        const linguisticComplexity = this.network.linguisticComplexity;
        const time = this.network.step / 60;
        const linguisticAngle = time * 1.2 + segment.id * 0.25;
        const linguisticMagnitude = linguisticComplexity * 0.3;
        
        return {
            x: Math.cos(linguisticAngle) * linguisticMagnitude,
            y: Math.sin(linguisticAngle) * linguisticMagnitude,
            z: Math.sin(linguisticAngle * 1.1) * linguisticMagnitude * 0.3,
            type: 'neural',
            magnitude: linguisticMagnitude,
            direction: linguisticAngle
        };
    }
    
    private calculateMysticalForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Mystical limbs are influenced by psychonaut creature
        const psychonautInfluence = this.calculatePsychonautInfluence();
        const time = this.network.step / 60;
        const mysticalAngle = time * 0.6 + segment.id * 0.4;
        const mysticalMagnitude = psychonautInfluence * 0.9;
        
        return {
            x: Math.cos(mysticalAngle) * mysticalMagnitude,
            y: Math.sin(mysticalAngle) * mysticalMagnitude,
            z: Math.cos(mysticalAngle * 0.8) * mysticalMagnitude * 0.7,
            type: 'neural',
            magnitude: mysticalMagnitude,
            direction: mysticalAngle
        };
    }
    
    private calculateTemporalForce(segment: LimbSegment, limb: Limb): PhysicsForce {
        // Temporal limbs are influenced by time-based patterns
        const time = this.network.step / 60;
        const temporalAngle = time * 0.4 + segment.id * 0.5;
        const temporalMagnitude = Math.sin(time * 0.3) * 0.4 + 0.4;
        
        return {
            x: Math.cos(temporalAngle) * temporalMagnitude,
            y: Math.sin(temporalAngle) * temporalMagnitude,
            z: Math.sin(temporalAngle * 1.4) * temporalMagnitude * 0.6,
            type: 'neural',
            magnitude: temporalMagnitude,
            direction: temporalAngle
        };
    }
    
    private applyForcesToSegment(segment: LimbSegment, forces: PhysicsForce[]): void {
        let totalForceX = 0;
        let totalForceY = 0;
        let totalForceZ = 0;
        
        for (const force of forces) {
            totalForceX += force.x;
            totalForceY += force.y;
            totalForceZ += force.z;
        }
        
        // Initialize velocities if not present
        if (!segment.vx) segment.vx = 0;
        if (!segment.vy) segment.vy = 0;
        if (!segment.vz) segment.vz = 0;
        
        // Apply forces to segment velocity
        segment.vx += totalForceX * 0.1;
        segment.vy += totalForceY * 0.1;
        segment.vz += totalForceZ * 0.1;
        
        // Apply damping
        segment.vx *= this.DAMPING;
        segment.vy *= this.DAMPING;
        segment.vz *= this.DAMPING;
    }
    
    private updateSegmentPosition(segment: LimbSegment, limb: Limb): void {
        // SCIENTIFIC MODEL: Verlet Integration
        // x(t + dt) = 2x(t) - x(t - dt) + a(t) * dt²
        // More accurate than Euler integration for spring-mass-damper systems
        
        const dt = 0.016; // Fixed timestep (60fps)
        const mass = segment.thickness; // Use thickness as proxy for mass
        
        // Initialize velocities if not present
        if (!segment.vx) segment.vx = 0;
        if (!segment.vy) segment.vy = 0;
        if (!segment.vz) segment.vz = 0;
        
        // Get forces from previous calculation
        const forces = this.lastCalculatedForces.get(`${limb.id}-${segment.id}`) || [];
        
        // Calculate total acceleration from forces
        let totalForceX = 0, totalForceY = 0, totalForceZ = 0;
        for (const force of forces) {
            totalForceX += force.x;
            totalForceY += force.y;
            totalForceZ += force.z;
        }
        
        const ax = totalForceX / mass;
        const ay = totalForceY / mass;
        const az = totalForceZ / mass;
        
        // Verlet integration: maintain previous positions for stability
        if (!(segment as any).x_prev) (segment as any).x_prev = segment.x;
        if (!(segment as any).y_prev) (segment as any).y_prev = segment.y;
        if (!(segment as any).z_prev) (segment as any).z_prev = segment.z;
        
        // Verlet algorithm: x_new = 2x - x_prev + a*dt²
        const x_new = 2 * segment.x - (segment as any).x_prev + ax * dt * dt;
        const y_new = 2 * segment.y - (segment as any).y_prev + ay * dt * dt;
        const z_new = 2 * segment.z - (segment as any).z_prev + az * dt * dt;
        
        // Update velocities from Verlet positions
        segment.vx = (x_new - segment.x) / dt;
        segment.vy = (y_new - segment.y) / dt;
        segment.vz = (z_new - segment.z) / dt;
        
        // Store previous positions
        (segment as any).x_prev = segment.x;
        (segment as any).y_prev = segment.y;
        (segment as any).z_prev = segment.z;
        
        // Update positions
        segment.x = x_new;
        segment.y = y_new;
        segment.z = z_new;
        
        // Breath-influenced motion: natural, not strictly in sync
        // Mix low-frequency noise with weak breath coupling and a small per-segment phase delay
        const breathing = this.network.breathingSystem?.getBreathingState?.();
        if (breathing) {
            const rootNode = this.network.nodes.find(n => n.id === limb.rootNodeId);
            if (rootNode) {
                // Direction outward from root
                const dx = segment.x - rootNode.x;
                const dy = segment.y - rootNode.y;
                const dz = (segment.z || 0) - (rootNode.z || 0);
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz) || 1;
                const ux = dx / dist, uy = dy / dist, uz = dz / dist;

                // Per-segment randomization and lag state
                const time = this.network.step / 60;
                if ((segment as any)._breathOffset === undefined) {
                    (segment as any)._breathOffset = Math.random() * Math.PI * 2;
                    (segment as any)._breathLag = 0; // smoothed expansion value
                }

                // Weak coupling to breath phase
                const phaseStrength = breathing.cyclePhase === 'inhale' ? 1 : (breathing.cyclePhase === 'pause_inhale' ? 0.4 : -0.2);
                const breathTarget = (breathing.breathDepth || 0) * phaseStrength;

                // Natural micro-motions (low-frequency noise)
                const noise = Math.sin(time * 0.7 + (segment as any)._breathOffset) * 0.3
                            + Math.sin(time * 0.23 + (segment as any)._breathOffset * 0.5) * 0.2;

                // Blend (non-strict sync): mostly noise with some breath influence
                const blended = 0.65 * noise + 0.35 * breathTarget;

                // Apply smoothing (lag) for organic feel
                const smooth = 0.92; // higher = smoother, slower response
                (segment as any)._breathLag = (segment as any)._breathLag * smooth + blended * (1 - smooth);

                // Final tiny displacement along limb direction
                const amplitude = 0.5; // keep subtle
                const expansion = (segment as any)._breathLag * amplitude;
                segment.x += ux * expansion;
                segment.y += uy * expansion;
                segment.z += uz * expansion * 0.6;
            }
        }
        
        // Apply constraints
        this.applySegmentConstraints(segment, limb);
        
        // Apply segment-to-segment repulsion to prevent collapse
        this.applySegmentRepulsion(segment, limb);
    }
    
    private applySegmentRepulsion(segment: LimbSegment, limb: Limb): void {
        // Prevent segments from getting too close to each other
        const minDistance = 15; // Minimum distance between segments
        
        for (const otherSegment of limb.segments) {
            if (otherSegment.id === segment.id) continue;
            
            const dx = segment.x - otherSegment.x;
            const dy = segment.y - otherSegment.y;
            const dz = segment.z - otherSegment.z;
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
            
            if (distance < minDistance && distance > 0.01) {
                // Repulsion force to push segments apart
                const repulsionStrength = (minDistance - distance) * 0.5;
                const forceX = (dx / distance) * repulsionStrength;
                const forceY = (dy / distance) * repulsionStrength;
                const forceZ = (dz / distance) * repulsionStrength * 0.5;
                
                // Apply repulsion to velocities
                segment.vx = (segment.vx || 0) + forceX;
                segment.vy = (segment.vy || 0) + forceY;
                segment.vz = (segment.vz || 0) + forceZ;
            }
        }
    }
    
    private applySegmentConstraints(segment: LimbSegment, limb: Limb): void {
        // Keep segments within reasonable bounds
        const maxDistance = 500;
        const centerX = 400;
        const centerY = 300;
        
        const distance = Math.sqrt(
            Math.pow(segment.x - centerX, 2) + 
            Math.pow(segment.y - centerY, 2)
        );
        
        if (distance > maxDistance) {
            const angle = Math.atan2(segment.y - centerY, segment.x - centerX);
            segment.x = centerX + Math.cos(angle) * maxDistance;
            segment.y = centerY + Math.sin(angle) * maxDistance;
        }
    }
    
    private applyLimbTypePhysics(segment: LimbSegment, limb: Limb): void {
        // Apply limb-specific physics behaviors
        switch (limb.type) {
            case 'fractal_branch':
                this.applyFractalPhysics(segment, limb);
                break;
            case 'psychic_manifest':
                this.applyPsychicPhysics(segment, limb);
                break;
            case 'wave_resonance':
                this.applyWavePhysics(segment, limb);
                break;
            case 'energy_conduit':
                this.applyEnergyPhysics(segment, limb);
                break;
            case 'linguistic_appendage':
                this.applyLinguisticPhysics(segment, limb);
                break;
            case 'mystical_tendril':
                this.applyMysticalPhysics(segment, limb);
                break;
            case 'temporal_phase':
                this.applyTemporalPhysics(segment, limb);
                break;
        }
    }
    
    private applyFractalPhysics(segment: LimbSegment, limb: Limb): void {
        // Fractal limbs exhibit self-similar patterns
        const time = this.network.step / 60;
        const fractalScale = 1 + Math.sin(time * 0.3 + segment.id * 0.1) * 0.2;
        segment.thickness *= fractalScale;
    }
    
    private applyPsychicPhysics(segment: LimbSegment, limb: Limb): void {
        // Psychic limbs can phase through normal physics
        const time = this.network.step / 60;
        const phaseShift = Math.sin(time * 2 + segment.id * 0.2) * 0.3;
        segment.x += phaseShift;
        segment.y += phaseShift * 0.5;
    }
    
    private applyWavePhysics(segment: LimbSegment, limb: Limb): void {
        // Wave limbs exhibit wave-like motion
        const time = this.network.step / 60;
        const waveOffset = Math.sin(time * 3 + segment.id * 0.3) * 5;
        segment.y += waveOffset;
    }
    
    private applyEnergyPhysics(segment: LimbSegment, limb: Limb): void {
        // Energy limbs conduct energy
        const energyTransfer = 0.1;
        segment.energy = Math.min(100, segment.energy + energyTransfer);
    }
    
    private applyLinguisticPhysics(segment: LimbSegment, limb: Limb): void {
        // Linguistic limbs respond to communication
        const communicationActivity = this.network.successfulCommunications;
        const response = Math.sin(communicationActivity * 0.1 + segment.id * 0.2) * 0.2;
        segment.x += response;
        segment.y += response * 0.7;
    }
    
    private applyMysticalPhysics(segment: LimbSegment, limb: Limb): void {
        // Mystical limbs are influenced by the psychonaut creature
        const psychonautInfluence = this.calculatePsychonautInfluence();
        const mysticalShift = psychonautInfluence * 2;
        segment.x += mysticalShift;
        segment.y += mysticalShift * 0.8;
        segment.z += mysticalShift * 1.2;
    }
    
    private applyTemporalPhysics(segment: LimbSegment, limb: Limb): void {
        // Temporal limbs can appear to move through time
        const time = this.network.step / 60;
        const temporalShift = Math.sin(time * 0.5 + segment.id * 0.4) * 3;
        segment.x += temporalShift;
        segment.y += temporalShift * 0.6;
    }
    
    private applyLimbLevelPhysics(limb: Limb): void {
        // Update limb-level properties
        limb.currentEnergy = Math.max(0, limb.currentEnergy - 0.1);
        
        // Update limb state based on energy
        if (limb.currentEnergy < limb.energyCapacity * 0.2) {
            limb.state = 'dormant';
        } else if (limb.currentEnergy > limb.energyCapacity * 0.8) {
            limb.state = 'active';
        }
        
        // Update effectiveness based on usage
        if (limb.state === 'active') {
            limb.effectiveness = Math.min(1, limb.effectiveness + 0.01);
        } else {
            limb.effectiveness = Math.max(0, limb.effectiveness - 0.005);
        }
    }
    
    private updateEnvironmentalInteractions(limb: Limb): void {
        // Update environmental field strengths based on network state
        for (const field of this.environmentalFields) {
            if (field.type === 'psychic') {
                field.strength = this.calculatePsychonautInfluence() * 0.5;
            } else if (field.type === 'electric') {
                field.strength = this.calculateEnergyFlow() * 0.3;
            }
        }
    }
    
    // Helper calculation methods
    private calculatePsychicPotential(): number {
        const energyFactor = this.network.totalNetworkEnergy / 1000;
        const linguisticFactor = this.network.linguisticComplexity / 10;
        return (energyFactor + linguisticFactor) / 2;
    }
    
    private calculateEnergyFlow(): number {
        return this.network.edges.reduce((sum, edge) => sum + edge.weight, 0) / 
               Math.max(1, this.network.edges.length);
    }
    
    private calculatePsychonautInfluence(): number {
        const distance = Math.sqrt(
            Math.pow(this.network.psychonautCreature.x - 400, 2) + 
            Math.pow(this.network.psychonautCreature.y - 300, 2)
        );
        return Math.max(0, 1 - distance / this.network.psychonautCreature.influenceRadius);
    }
    
    // Public interface methods
    public addEnvironmentalField(field: EnvironmentalField): void {
        this.environmentalFields.push(field);
    }
    
    public removeEnvironmentalField(index: number): void {
        this.environmentalFields.splice(index, 1);
    }
    
    public getEnvironmentalFields(): EnvironmentalField[] {
        return [...this.environmentalFields];
    }
    
    public getLimbPhysicsState(limbId: number): PhysicsState | undefined {
        return this.physicsStates.get(limbId);
    }
    
    public setPhysicsConstants(constants: Partial<{
        gravity: number;
        elasticity: number;
        damping: number;
        inertia: number;
        friction: number;
        buoyancy: number;
    }>): void {
        if (constants.gravity !== undefined) this.GRAVITY = constants.gravity;
        if (constants.elasticity !== undefined) this.ELASTICITY = constants.elasticity;
        if (constants.damping !== undefined) this.DAMPING = constants.damping;
        if (constants.inertia !== undefined) this.INERTIA = constants.inertia;
        if (constants.friction !== undefined) this.FRICTION = constants.friction;
        if (constants.buoyancy !== undefined) this.BUOYANCY = constants.buoyancy;
    }
}
